<template>
	<nav ref="navbarRef" v-pointer:pointershift class="_navbar no-select" data-motion-fallback>
		<div class="fixed top-0 left-0 w-full-vw h-full-vh" :inert="!isOpen" @click="close" />
		<div aria-hidden="true" class="click-catchers">
			<div @click="onOpenClick" />
			<div @click="onOpenClick" />
		</div>
		<div class="bottom-click-catcher fixed bottom-0" aria-hidden="true" @click="onOpenClick" />
		<div />
		<div class="persistent-bar">
			<div class="persistent-bar-main flex justify-space-between align-center w-full h-full">
				<div class="bot-container m-c w-full">
					<Bot :animate="!isOpen && !isAnimating" />
				</div>
				<div class="active-page">
					<RootPageName
						v-if="mounted && screen.width > breakpoints.xsmall"
						:animate="!isOpen && !isAnimating"
					/>
				</div>
			</div>
			<button class="open-close-button" aria-label="Open/close menu" @click="onOpenClick">
				<OpenCloseIcon as="div" :open="isOpen" />
				<span>Open/close menu</span>
			</button>
		</div>
		<div class="menu" :inert="!isOpen">
			<div class="navbar-background blurred-background" />
			<div class="menu-content-container">
				<div class="menu-content no-scrollbar" data-lenis-prevent>
					<!--  -->
					<div class="menu-header">
						<div class="title">
							<nuxt-link to="/">
								<svg
									class="_buro-flag"
									width="48"
									height="48"
									viewBox="0 0 48 48"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
									:data-color="'white'"
								>
									<path
										d="M45.9629 5.62317H4.23822L1.96289 7.14031V23.8296L45.9629 7.14031V5.62317Z"
									/>
									<path
										d="M4.23822 42.0367L45.9621 25.3475V23.8296H1.96289V40.5196L4.23822 42.0367Z"
									/>
								</svg>
							</nuxt-link>
							<p class="tagline -p-large whitespace-preline">{{ tagline }}</p>
						</div>
						<LetsTalkCta />
					</div>
					<!--  -->
					<!--  -->
					<ul class="menu-pages">
						<li v-for="(page, index) in pages" :key="index">
							<div v-if="index === 0" class="divider -top" />
							<PageLink
								:link="page.link"
								:label="page.label"
								:thumbnail="page.thumbnail"
								:children="page.children"
								:children-grid-count="maxChildrenCount"
								:chapter-label="page.chapterLabel"
								:years="page.years"
							/>
							<div class="divider m-t" />
						</li>
					</ul>
					<!--  -->
					<!--  -->
					<ul v-if="!showBookCta" class="menu-socials">
						<li v-for="(social, index) in socials" :key="index" class="m-c">
							<Hyperlink :to="social.link" class="social-link m-t hover-opacity">
								<span class="relative -p-tiny">{{ social.label }}</span>
							</Hyperlink>
						</li>
					</ul>
					<div v-else class="menu-socials -book">
						<ImageAsset :asset="book.thumbnail" class="book-thumb" />
						<ul class="menu-book-wrap">
							<p
								class="-p-small social-link book-copy ng-inset-xxlarge-3 ng-xlarge-4 ng-inset-large-4 ng-inset-medium-5 ng-inset-small-8 ng-inset-xsmall-12 ng-xxsmall-14 m-c"
							>
								{{ book.copy }}
							</p>
							<span class="-p-medium social-link book-link m-c">
								<svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
									<path
										d="M12 7.5L13 6V4.5L12 4L10.5 5L8 6L4 10.7703L2 11L3 19.5L5 19L12 20.5L15.5 19.5L16 18L15.5 17.5H13.5V17H16.5L17 16.5V15L16.5 14.5H14V14H17.5L18 13.5V12L17.5 11.5H14.5V11H21.5L22 10.5V9L21.5 8.5L14 8L10 8.5L12 7.5Z"
										fill="white"
									/>
								</svg>
								<a target="_blank" class="m-t hover-opacity" :href="book.link">{{
									book.linkCopy
								}}</a>
							</span>
						</ul>
					</div>
					<!--  -->
				</div>
			</div>
		</div>
	</nav>
</template>

<script setup>
import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from '#app'
import { useScreen } from 'vue-screen'
import PageLink from './components/Navbar.PageLink'
import LetsTalkCta from './components/Navbar.LetsTalkCta'
import RootPageName from './components/Navbar.RootPageName'
import OpenCloseIcon from './components/Navbar.OpenCloseIcon'
import { Bot, BotInstance } from '@/components/navigation/Bot'
import { createNavbarEmitter } from './Navbar.emitter'
import { createNavbarMotion } from './Navbar.motion'
import { HyperLink as Hyperlink, ImageAsset, getActiveContext } from '@/components/core'
import { createKeyStack } from '@/utils/navigation/keyboard'
import { lockBodyScroll } from '@/utils/navigation/bodyScrollLock'
import { breakpoints } from '@/config'

const props = defineProps({
	initOpen: { type: Boolean, default: false },
	tagline: { type: String, required: true },
	pages: { type: Array, required: true },
	socials: { type: Array, required: true },
	bookCta: {
		type: [Boolean, Object],
		default: false,
	},
})

const DEFAULT_BOOK_CTA = {
	copy: 'Would you like our story in your bookshelf?',
	linkCopy: 'Pre-order the book',
	link: 'https://book.burocratik.com/',
	thumbnail: {
		url: '/imgs/burobook-thumb.png',
		type: 'image',
		extension: 'image/png',
		width: 98,
		height: 160,
	},
}

const route = useRoute()
const screen = useScreen()
const emitter = createNavbarEmitter()
const scroller = inject('$scroller')
const navbarRef = ref(null)
const motion = ref(null)
const revertBotOpenMessage = ref(null)
const isOpen = ref(props.initOpen)
const isAnimating = ref(false)
const mounted = ref(false)

let escapeStack = null
let removeEscapeHandler = null
let unlockScroll = null
let botOpenEvent = null

const showBookCta = computed(() => !!props.bookCta)
const book = computed(() => ({
	...DEFAULT_BOOK_CTA,
	...(typeof props.bookCta === 'object' ? props.bookCta : {}),
}))
const maxChildrenCount = computed(() =>
	props.pages.reduce(
		(count, page) => Math.max(count, page.chapterLabel ? 0 : page.children?.length || 0),
		0,
	),
)

function qs(query) {
	return navbarRef.value ? navbarRef.value.querySelector(query) : null
}

function qsa(query) {
	return navbarRef.value ? [...navbarRef.value.querySelectorAll(query)] : []
}

function getScrollerControl() {
	return scroller?.value ?? scroller
}

function stopPageScroll() {
	if (unlockScroll) return

	const scrollerControl = getScrollerControl()

	if (scrollerControl?.stop) {
		scrollerControl.stop()
		unlockScroll = () => scrollerControl.start?.()
		return
	}

	if (scrollerControl?.lock) {
		scrollerControl.lock()
		unlockScroll = () => scrollerControl.unlock?.()
		return
	}

	unlockScroll = lockBodyScroll()
}

function startPageScroll() {
	unlockScroll?.()
	unlockScroll = null
}

function close(options) {
	if (!isOpen.value) return

	emitter.emit('request-navbar-open', false)
	startPageScroll()
	removeEscapeHandler?.()
	removeEscapeHandler = null

	if (motion.value) {
		isAnimating.value = true
		motion.value.menu.close(options).then(() => {
			isAnimating.value = false
		})
	}

	isOpen.value = false
}

function open() {
	if (isOpen.value) return

	emitter.emit('request-navbar-open', true)
	stopPageScroll()
	removeEscapeHandler = escapeStack?.push(close)

	if (revertBotOpenMessage.value) {
		revertBotOpenMessage.value()
		revertBotOpenMessage.value = null
	}

	if (motion.value) {
		isAnimating.value = true
		motion.value.menu.open().then(() => {
			isAnimating.value = false
		})
	}

	isOpen.value = true
}

function onOpenClick() {
	if (isOpen.value) {
		close()
	} else {
		open()
	}
}

watch(
	() => route.name,
	() => {
		close()
	},
)

onMounted(() => {
	escapeStack = createKeyStack({ key: 'escape' })

	nextTick(() => {
		motion.value = createNavbarMotion(
			{ bot: BotInstance },
			{
				container: navbarRef.value,
				persistentBar: {
					container: qs('.persistent-bar'),
					main: qs('.persistent-bar-main'),
					bot: qs('.bot-container'),
					pageIndicator: qs('.active-page'),
				},
				menu: {
					container: qs('.menu'),
					content: qs('.menu-content'),
					clickables: qsa('.menu-header, .menu-pages, .menu-socials'),
					pages: {
						containers: qsa('.menu-pages > li'),
						labels: qsa('.menu-pages > li .label .m-t'),
					},
					dividers: {
						horizontal: qsa('.divider'),
						vertical: qsa('.children-divider'),
					},
					socials: qsa('.menu-socials .m-t'),
				},
				background: qs('.navbar-background'),
				getPage: () => getActiveContext(),
			},
		)

		if (isOpen.value) {
			stopPageScroll()
			removeEscapeHandler = escapeStack.push(close)

			if (motion.value) {
				motion.value.menu.open({ immediate: true })
			}
		} else if (motion.value) {
			motion.value.menu.close({ immediate: true })
		}

		if (!screen.touch) {
			botOpenEvent = BotInstance.events.setup({
				type: 'custom',
				icon: 'arrow-up',
				message: 'Open menu',
				event: (payload, { commit }) => {
					const button = qs('.open-close-button')

					function onMouseEnter() {
						revertBotOpenMessage.value?.()
						revertBotOpenMessage.value = commit()
					}

					function onMouseLeave() {
						revertBotOpenMessage.value?.()
						revertBotOpenMessage.value = null
					}

					button?.addEventListener('mouseenter', onMouseEnter)
					button?.addEventListener('mouseleave', onMouseLeave)

					return {
						destroy() {
							onMouseLeave()
							button?.removeEventListener('mouseenter', onMouseEnter)
							button?.removeEventListener('mouseleave', onMouseLeave)
						},
					}
				},
			})
		}

		mounted.value = true

		emitter.on('request-navbar-close', close)
	})
})

onBeforeUnmount(() => {
	if (botOpenEvent) {
		BotInstance.events.remove(botOpenEvent.id)
		botOpenEvent = null
	}
	revertBotOpenMessage.value?.()
	revertBotOpenMessage.value = null
	removeEscapeHandler?.()
	escapeStack?.destroy()
	startPageScroll()
	emitter.off('request-navbar-close', close)
	motion.value?.destroy()
})
</script>

<style lang="scss" scoped>
@use './atoms.scss';

._navbar {
	--navbar-width-cols: 10;
	--navbar-viewport-width: var(--100vw, calc(100vw - var(--sbw, 0px)));
	--navbar-grid-gap: var(--grid-gap, #{rs(20, 4)});
	--navbar-grid-margin: var(--grid-margin, #{rs(20, 8)});
	--navbar-grid-padding: var(
		--grid-padding,
		calc(var(--navbar-grid-margin) - 0.5 * var(--navbar-grid-gap))
	);
	--navbar-width: calc(
		var(--navbar-width-cols) *
			((var(--navbar-viewport-width) - 2 * var(--navbar-grid-padding)) / 24) -
			var(--navbar-grid-gap)
	);
	--navbar-height: #{rs(46, 46)};
	--navbar-border-radius: #{rs(20, 20)};
	--navbar-bottom: #{rs(32, 24)};

	@include max-width($md) {
		--navbar-width-cols: 12;
	}
	@include max-width($sm) {
		--navbar-width-cols: 18;
	}
	@include max-width($xs) {
		--navbar-width-cols: 22;
	}

	$left: calc(0.5 * (var(--navbar-viewport-width) - var(--navbar-width)));

	position: fixed;
	width: var(--navbar-width);
	height: var(--navbar-height);
	left: $left;
	bottom: var(--navbar-bottom);

	color: $white;
	z-index: 9;

	.-p-large {
		font-size: clamp(24px, 22.206px + 100vw * 0.0046, 30px);
		line-height: 1.1;
		letter-spacing: -0.05em;
		---letter-spacing: -0.05em;
	}

	.menu-header {
		._buro-flag {
			--default-color: #{$black};

			fill: var(--color, var(--default-color));

			&[data-color='white'] {
				--default-color: #{$white};
			}
		}
	}

	// extend hitarea to the bottom of the viewport
	// avoid accidentally clicking below it on mobile
	.bottom-click-catcher {
		width: var(--navbar-width);
		height: var(--navbar-bottom);
		opacity: 0;
	}

	&[data-motion-fallback] {
		.menu {
			&:not([inert]) {
				.navbar-background {
					height: 100%;
					z-index: 0;
				}

				.menu-content {
					visibility: visible;
				}
			}

			&[inert] {
				.menu-content {
					visibility: hidden;
				}
			}
		}
	}

	.navbar-background {
		position: absolute;
		width: 100%;
		height: var(--navbar-height);
		left: 0;
		bottom: 0;

		border-radius: var(--navbar-border-radius);

		z-index: -1;
		will-change: height;
	}
	.blurred-background {
		background: linear-gradient(0deg, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.85) 100%),
			rgba(255, 255, 255, 0.4);
		backdrop-filter: blur(18px);
		-webkit-backdrop-filter: blur(18px);
	}

	.persistent-bar {
		position: relative;
		width: 100%;
		height: 100%;

		display: flex;
		gap: rs(14, 14);
		justify-content: space-between;
		align-items: center;
		padding: 0 rs(16, 16);

		overflow: hidden;

		&-main {
			.bot-container {
				flex-grow: 1;
				min-width: 0;
			}
		}

		.open-close-button {
			--color: #{$white};

			position: relative;
			z-index: 1;
			flex-shrink: 0;

			// extends hit area
			& > span {
				position: absolute;
				width: calc(var(--navbar-width) + 16px);
				height: calc(100% + 32px);
				right: -24px;
				top: -16px;

				overflow: hidden;
				opacity: 0;
			}
		}
	}

	.menu {
		--x-padding: #{rs(20, 20)};

		position: absolute;
		width: 100%;
		left: 0;
		bottom: 0;

		border-radius: var(--navbar-border-radius);

		&-content-container {
			position: relative;

			overflow: hidden;
			mask-image: linear-gradient(180deg, rgba($black, 1) calc(100% - 16px), rgba($black, 0) 100%);

			z-index: 2;
			pointer-events: none;
		}

		&-header,
		&-pages,
		&-socials {
			pointer-events: all;
		}

		&-content {
			max-height: calc(100svh - 2 * var(--navbar-bottom));
			min-height: 80lvh;

			display: flex;
			flex-direction: column;

			overflow-y: scroll;
			will-change: transform;
		}

		&-header {
			position: relative;

			display: flex;
			justify-content: space-between;
			padding: rs(30, 30) var(--x-padding);

			._buro-logo {
				width: rs(46, 46);
				height: max-content;
			}

			.tagline {
				grid-row: 2;
				grid-column: 1;

				margin-top: rs(6, 6);
				white-space: pre-line;

				@include max-width($xs) {
					display: none;
				}
			}
		}

		&-pages {
			position: relative;

			display: flex;
			flex-direction: column;
		}

		&-socials {
			position: relative;
			width: max-content;
			margin-top: auto;

			padding-top: rs(24, 24);
			padding-bottom: rs(16, 16);

			.social-link {
				position: relative;

				padding: rs(2, 2) var(--x-padding);
				z-index: 3;

				@include hover-supported {
					& > span {
						display: inline-block;
						transform: translateX(0px);
						transition: transform 0.175s ease('power4');
					}

					&:hover > span {
						transform: translateX(0.5em);
					}
				}
			}

			&.-book {
				display: flex;
				padding: rs(2, 2) var(--x-padding);
				padding-bottom: rs(10, 10);
				width: column-width(5, true);

				.book-thumb {
					width: rs(49, 42);
					height: rs(80, 68);
				}
				.menu-book-wrap {
					.book-copy {
						padding-bottom: rs(8, 8);
						opacity: 0.4;
					}

					.book-link {
						display: flex;
						flex-direction: row;
						justify-content: flex-start;
						align-items: center;
						gap: rs(6, 6);
						transform: translateX(0px);
						transition: transform 0.175s ease('power4');

						@include hover-supported {
							&:hover {
								transform: translateX(0.5em);
							}
						}
					}

					.-p-medium {
						font-family: 'Commercial Gräphik Web';
						font-size: rs(24, 20);
						letter-spacing: -0.04em;
						line-height: 1;
						font-weight: 400;
						@include max-width($sm) {
							font-size: rs(24, 20);
						}
					}

					.-p-small {
						font-family: 'Commercial Gräphik Web';
						font-size: rs(18, 16);
						letter-spacing: -0.04em;
						line-height: 1.2;
						font-weight: 400;
						@include max-width($sm) {
							font-size: rs(18, 16);
						}
					}
				}
			}
		}
	}

	.divider {
		position: absolute;
		width: 100%;
		height: 1px;
		left: 0;
		bottom: 0;

		background-color: rgba($white, 0.06);

		&.-top {
			top: 0;
			bottom: unset;
		}
	}

	li {
		position: relative;
	}
}
</style>
